import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus, Save, Trash2, X, Loader2, Share2, Eye,
  Copy, Pencil, Filter as FilterIcon, Crosshair, FileSpreadsheet,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { WIDGET_CATALOG, EMPTY_FILTERS } from '../components/analyses/widgetCatalog';
import { api, newId, mergeFilters, widgetHasFilters, selectionsByDim, selectedKeySet, selectionsToFilters } from '../components/analyses/analysesUtils';
import { Field } from '../components/analyses/Field';
import { WIDGET_RENDERERS } from '../components/analyses/WidgetRenderers';
import { exportAnalysesToExcel } from '../components/analyses/exportAnalyses';

const WidgetCard = ({ widget, globalFilters, onRemove, onEdit, onDuplicate, isEditing, ctx, hasWidgetFilter, onClearFilter }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const meta = WIDGET_CATALOG.find(w => w.type === widget.type);
  const Renderer = WIDGET_RENDERERS[widget.type];
  const effFilters = useMemo(() => mergeFilters(globalFilters, widget.filters), [globalFilters, widget.filters]);
  // ctx propre au widget : on injecte l'id du widget dans toggleSelect pour
  // pouvoir rattacher chaque filtre/sélection au widget qui l'a déclenché.
  const widgetCtx = useMemo(() => (
    ctx && ctx.toggleSelect
      ? { ...ctx, toggleSelect: (sel) => ctx.toggleSelect(sel, widget.id) }
      : ctx
  ), [ctx, widget.id]);

  useEffect(() => {
    let aborted = false;
    setData(null);
    setError(null);
    api('/analytics/widget-data', {
      method: 'POST',
      body: JSON.stringify({ type: widget.type, filters: effFilters }),
    })
      .then(r => { if (!aborted) setData(r.data); })
      .catch(e => { if (!aborted) setError(e.message); });
    return () => { aborted = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget.type, JSON.stringify(effFilters)]);

  const colSpan = meta?.w === 12 ? 'md:col-span-12' : meta?.w === 6 ? 'md:col-span-6' : 'md:col-span-4';
  return (
    <div className={`bg-white border border-[#BFC6CC] rounded-xl p-4 ${colSpan} col-span-12`} data-testid={`widget-${widget.type}`}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="text-sm font-semibold text-[#191D1E] flex items-center gap-2 min-w-0">
          {meta?.icon && <meta.icon className="w-4 h-4 text-[#397DFF] shrink-0" />}
          <span className="truncate">{widget.label || meta?.label || widget.type}</span>
          {widgetHasFilters(widget) && (
            <span className="shrink-0 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-[#EEF2F6] text-[#397DFF] rounded" title="Ce widget a ses propres filtres">
              <FilterIcon className="w-2.5 h-2.5" /> filtré
            </span>
          )}
        </h3>
        <div className="flex items-center gap-1 shrink-0">
          {hasWidgetFilter && (
            <button onClick={onClearFilter}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 bg-blue-50 text-[#397DFF] border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
              data-testid={`widget-clear-filter-${widget.id}`}
              title="Retirer le filtre appliqué depuis ce widget">
              <X className="w-3 h-3" /> Retirer le filtre
            </button>
          )}
          {isEditing && (
            <>
              <button onClick={onEdit} className="text-[#687175] hover:bg-[#F5F6F9] p-1 rounded" title="Configurer" data-testid={`widget-edit-${widget.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button onClick={onDuplicate} className="text-[#687175] hover:bg-[#F5F6F9] p-1 rounded" title="Dupliquer" data-testid={`widget-duplicate-${widget.id}`}>
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button onClick={onRemove} className="text-red-500 hover:bg-red-50 p-1 rounded" title="Supprimer" data-testid={`widget-remove-${widget.id}`}>
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
      {error && <div className="text-xs text-red-600 py-4 text-center">{error}</div>}
      {!error && !data && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-[#397DFF]" />
        </div>
      )}
      {!error && data && Renderer && <Renderer data={data} ctx={widgetCtx} />}
    </div>
  );
};

// ---------- BUILDER MODAL ----------
const WidgetBuilder = ({ initial, agences, commerciaux, onCancel, onSave }) => {
  const [type, setType] = useState(initial?.type || 'kpis');
  const [label, setLabel] = useState(initial?.label || '');
  const [f, setF] = useState({ ...EMPTY_FILTERS, ...(initial?.filters || {}) });
  const meta = WIDGET_CATALOG.find(w => w.type === type);
  const comFiltered = commerciaux.filter(c => !f.agence_id || String(c.agence_id) === String(f.agence_id));
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="widget-builder">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            {meta?.icon && <meta.icon className="w-5 h-5 text-[#397DFF]" />}
            {initial?.id ? 'Configurer le widget' : 'Nouveau widget personnalisé'}
          </h2>
          <button onClick={onCancel} className="text-[#687175] hover:text-[#191D1E]"><X className="w-5 h-5" /></button>
        </div>

        <Field label="Type de widget">
          <select value={type} onChange={(e) => setType(e.target.value)}
            className="w-full px-3 py-2 border border-[#BFC6CC] rounded text-sm bg-white mb-1" data-testid="builder-type">
            {WIDGET_CATALOG.map(w => <option key={w.type} value={w.type}>{w.label}</option>)}
          </select>
        </Field>
        <p className="text-xs text-[#687175] mb-4">{meta?.description}</p>

        <Field label="Nom personnalisé (optionnel)">
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder={meta?.label}
            className="w-full px-3 py-2 border border-[#BFC6CC] rounded text-sm mb-4" data-testid="builder-label" />
        </Field>

        <p className="text-xs font-semibold text-[#191D1E] mb-2 flex items-center gap-1.5">
          <FilterIcon className="w-3.5 h-3.5" /> Filtres propres à ce widget
          <span className="font-normal text-[#687175]">(combinés aux filtres globaux de la page)</span>
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
          <Field label="Taxe">
            <select value={f.taxe} onChange={(e) => set('taxe', e.target.value)}
              className="w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-sm bg-white" data-testid="builder-taxe">
              <option value="">Toutes</option>
              <option value="TICFE">TICFE</option>
              <option value="TICGN">TICGN</option>
              <option value="TURPE">TURPE</option>
            </select>
          </Field>
          <Field label="Du"><input type="date" value={f.date_debut} onChange={(e) => set('date_debut', e.target.value)} className="w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-sm" data-testid="builder-date-debut" /></Field>
          <Field label="Au"><input type="date" value={f.date_fin} onChange={(e) => set('date_fin', e.target.value)} className="w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-sm" data-testid="builder-date-fin" /></Field>
          <Field label="Agence">
            <select value={f.agence_id} onChange={(e) => setF(prev => ({ ...prev, agence_id: e.target.value, commercial_id: '' }))}
              className="w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-sm bg-white" data-testid="builder-agence">
              <option value="">Toutes</option>
              {agences.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
            </select>
          </Field>
          <Field label="Commercial">
            <select value={f.commercial_id} onChange={(e) => set('commercial_id', e.target.value)}
              className="w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-sm bg-white" data-testid="builder-commercial">
              <option value="">Tous</option>
              {comFiltered.map(c => <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>)}
            </select>
          </Field>
          <Field label="Éligibilité">
            <select value={f.eligibilite} onChange={(e) => set('eligibilite', e.target.value)}
              className="w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-sm bg-white" data-testid="builder-eligibilite">
              <option value="">Toutes</option>
              <option value="ELIGIBLE">Éligible</option>
              <option value="NON_ELIGIBLE">Non éligible</option>
              <option value="A_APPROFONDIR">À approfondir</option>
            </select>
          </Field>
          <Field label="Validé client">
            <select value={f.valide_client} onChange={(e) => set('valide_client', e.target.value)}
              className="w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-sm bg-white" data-testid="builder-valide-client">
              <option value="">Tous</option>
              <option value="oui">Validés client</option>
              <option value="non">Non validés</option>
            </select>
          </Field>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-[#BFC6CC] rounded hover:bg-[#F5F6F9]">Annuler</button>
          <button onClick={() => onSave({ type, label: label.trim(), filters: f })}
            className="px-4 py-2 text-sm bg-[#397DFF] text-white rounded hover:bg-[#2E5BCC]" data-testid="builder-save">
            {initial?.id ? 'Appliquer' : 'Ajouter le widget'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Analyses = () => {
  const { user } = useAuth();
  const [views, setViews] = useState([]);
  const [currentViewId, setCurrentViewId] = useState(null);
  const [viewName, setViewName] = useState('Vue par défaut');
  const [widgets, setWidgets] = useState([]);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [agences, setAgences] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [builder, setBuilder] = useState(null); // { mode:'add'|'edit', index, initial }
  const [isEditing, setIsEditing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [sharedWithAgence, setSharedWithAgence] = useState(false);
  const [selections, setSelections] = useState([]);   // [{dim,id,label}] cumulatif
  const [crossFilter, setCrossFilter] = useState(true); // true=filtrage réel (par défaut), false=surlignage
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api('/analytics/views').then(vs => {
      setViews(vs);
      // Recharge automatiquement la dernière vue ouverte (sinon le dashboard
      // retombait sur les widgets par défaut après un rafraîchissement → le
      // widget ajouté semblait "disparaître").
      const lastId = localStorage.getItem('analyses_view_id');
      const v = lastId ? vs.find(x => x.id === lastId) : null;
      if (v) {
        setCurrentViewId(v.id);
        setViewName(v.nom);
        setWidgets((v.widgets || []).map(w => ({ id: w.id || newId(), type: w.type, label: w.label || '', filters: w.filters || {} })));
        setFilters({ ...EMPTY_FILTERS, ...(v.filters || {}) });
        setSharedWithAgence(!!v.shared_with_agence_id);
      }
    }).catch(() => {});
    api('/agences').then(setAgences).catch(() => {});
    api('/commerciaux').then(setCommerciaux).catch(() => {});
  }, []);

  useEffect(() => {
    if (widgets.length === 0 && !currentViewId) {
      setWidgets([
        { id: newId(), type: 'kpis' },
        { id: newId(), type: 'dossiers_par_mois' },
        { id: newId(), type: 'economies_par_type' },
      ]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewId]);

  const commToAgence = useMemo(() => {
    const m = {};
    commerciaux.forEach(c => { m[String(c.id)] = String(c.agence_id); });
    return m;
  }, [commerciaux]);

  // normalise les widgets chargés (rétro-compat : ajoute un id si absent)
  const normalizeWidgets = (ws) => (ws || []).map(w => ({ id: w.id || newId(), type: w.type, label: w.label || '', filters: w.filters || {} }));

  const loadView = (v) => {
    setCurrentViewId(v.id);
    setViewName(v.nom);
    setWidgets(normalizeWidgets(v.widgets));
    setFilters({ ...EMPTY_FILTERS, ...(v.filters || {}) });
    setSharedWithAgence(!!v.shared_with_agence_id);
    setSelections([]); setCrossFilter(true);
    localStorage.setItem('analyses_view_id', v.id);
  };

  const newView = () => {
    setCurrentViewId(null);
    setViewName('Nouvelle vue');
    setWidgets([{ id: newId(), type: 'kpis' }]);
    setSharedWithAgence(false);
    setSelections([]); setCrossFilter(true);
    localStorage.removeItem('analyses_view_id');
  };

  const removeWidget = (idx) => setWidgets(prev => prev.filter((_, i) => i !== idx));
  const duplicateWidget = (idx) => setWidgets(prev => {
    const copy = { ...prev[idx], id: newId(), label: (prev[idx].label || WIDGET_CATALOG.find(w => w.type === prev[idx].type)?.label || '') + ' (copie)' };
    const next = [...prev];
    next.splice(idx + 1, 0, copy);
    return next;
  });

  const saveBuilder = (config) => {
    if (builder?.mode === 'edit') {
      setWidgets(prev => prev.map((w, i) => i === builder.index ? { ...w, ...config } : w));
    } else {
      setWidgets(prev => [...prev, { id: newId(), ...config }]);
    }
    setBuilder(null);
  };

  const saveView = async () => {
    setSaving(true); setFeedback(null);
    try {
      const payload = { nom: viewName, widgets, filters,
        shared_with_agence_id: sharedWithAgence ? (user?.agence_id || '') : null };
      let saved;
      if (currentViewId) {
        saved = await api(`/analytics/views/${currentViewId}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        saved = await api('/analytics/views', { method: 'POST', body: JSON.stringify(payload) });
        setCurrentViewId(saved.id);
      }
      localStorage.setItem('analyses_view_id', saved.id);
      const refreshed = await api('/analytics/views');
      setViews(refreshed);
      setFeedback({ type: 'success', message: 'Vue enregistrée.' });
    } catch (e) {
      setFeedback({ type: 'error', message: e.message });
    } finally { setSaving(false); }
  };

  const deleteView = async () => {
    if (!currentViewId || !window.confirm('Supprimer cette vue ?')) return;
    try {
      await api(`/analytics/views/${currentViewId}`, { method: 'DELETE' });
      const refreshed = await api('/analytics/views');
      setViews(refreshed); newView();
    } catch (e) { setFeedback({ type: 'error', message: e.message }); }
  };

  const commerciauxFiltered = useMemo(() =>
    commerciaux.filter(c => !filters.agence_id || String(c.agence_id) === String(filters.agence_id)),
    [commerciaux, filters.agence_id]);

  // Toggle d'une sélection (clic simple) → filtrage par défaut (crossFilter), ou surlignage.
  // widgetId : id du widget d'origine, pour rattacher le filtre au widget cliqué.
  const toggleSelect = (sel, widgetId) => {
    setSelections(prev => {
      const exists = prev.some(s => s.dim === sel.dim && String(s.id) === String(sel.id));
      return exists
        ? prev.filter(s => !(s.dim === sel.dim && String(s.id) === String(sel.id)))
        : [...prev, { dim: sel.dim, id: String(sel.id), label: sel.label, widgetId: widgetId || null }];
    });
  };
  const removeSelection = (s) => setSelections(prev => prev.filter(x => !(x.dim === s.dim && String(x.id) === String(s.id))));
  const clearSelections = () => { setSelections([]); };
  // Retire toutes les sélections déclenchées depuis un widget donné.
  const clearWidgetFilter = (widgetId) => setSelections(prev => prev.filter(s => s.widgetId !== widgetId));
  const widgetsWithSelection = useMemo(
    () => new Set(selections.map(s => s.widgetId).filter(Boolean)),
    [selections]
  );

  const handleExportExcel = async () => {
    setExporting(true); setFeedback(null);
    try {
      await exportAnalysesToExcel({
        widgets,
        globalFilters: effectiveFilters,
        selections: crossFilter ? selections : [],
        viewName,
      });
    } catch (e) {
      setFeedback({ type: 'error', message: `Échec de l'export : ${e.message}` });
    } finally { setExporting(false); }
  };

  // Filtres effectifs envoyés aux widgets : on n'injecte la sélection que si crossFilter est actif.
  const effectiveFilters = useMemo(
    () => (crossFilter && selections.length ? { ...filters, ...selectionsToFilters(selections) } : filters),
    [crossFilter, selections, filters]
  );

  // Surlignage (byDim) : actif uniquement hors mode filtrage (sinon les données sont déjà filtrées).
  const ctx = useMemo(() => ({
    byDim: crossFilter ? {} : selectionsByDim(selections),
    selectedKeys: selectedKeySet(selections),
    toggleSelect,
    crossFilter,
    commToAgence,
  }), [selections, crossFilter, commToAgence]);

  const DIM_LABEL = { agence: 'Agence', commercial: 'Commercial', taxe: 'Taxe', mois: 'Mois', eligibilite: 'Éligibilité', valide_client: 'Validation' };

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="analyses-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-[#191D1E]">Analyses</h1>
          <p className="text-sm text-[#687175] mt-1">Tableau de bord BI dynamique avec widgets personnalisables et interactifs.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={currentViewId || ''}
            onChange={(e) => { const v = views.find(x => x.id === e.target.value); v ? loadView(v) : newView(); }}
            className="px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm bg-white" data-testid="view-selector">
            <option value="">Nouvelle vue…</option>
            {views.map(v => (
              <option key={v.id} value={v.id}>
                {v.nom}{v.user_id !== user?.id ? ' (partagée)' : ''}
              </option>
            ))}
          </select>
          <button onClick={() => setIsEditing(!isEditing)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-[#BFC6CC] rounded-lg hover:bg-[#F5F6F9]" data-testid="toggle-edit">
            <Eye className="w-4 h-4" /> {isEditing ? 'Aperçu' : 'Modifier'}
          </button>
          <button onClick={handleExportExcel} disabled={exporting || widgets.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-[#10B981] text-white rounded-lg hover:bg-[#059669] disabled:opacity-50" data-testid="export-excel-btn"
            title="Exporter la vue filtrée au format Excel">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            {exporting ? 'Export…' : 'Exporter Excel'}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#BFC6CC] rounded-xl p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 text-sm">
          <Field label="Du">
            <input type="date" value={filters.date_debut} onChange={(e) => setFilters({ ...filters, date_debut: e.target.value })}
              className="w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-sm" data-testid="filter-date-debut" />
          </Field>
          <Field label="Au">
            <input type="date" value={filters.date_fin} onChange={(e) => setFilters({ ...filters, date_fin: e.target.value })}
              className="w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-sm" data-testid="filter-date-fin" />
          </Field>
          <Field label="Agence">
            <select value={filters.agence_id} onChange={(e) => setFilters({ ...filters, agence_id: e.target.value, commercial_id: '' })}
              className="w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-sm bg-white" data-testid="filter-agence">
              <option value="">Toutes</option>
              {agences.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
            </select>
          </Field>
          <Field label="Commercial">
            <select value={filters.commercial_id} onChange={(e) => setFilters({ ...filters, commercial_id: e.target.value })}
              className="w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-sm bg-white" data-testid="filter-commercial">
              <option value="">Tous</option>
              {commerciauxFiltered.map(c => <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>)}
            </select>
          </Field>
          <Field label="Éligibilité">
            <select value={filters.eligibilite} onChange={(e) => setFilters({ ...filters, eligibilite: e.target.value })}
              className="w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-sm bg-white" data-testid="filter-eligibilite">
              <option value="">Toutes</option>
              <option value="ELIGIBLE">Éligible</option>
              <option value="NON_ELIGIBLE">Non éligible</option>
              <option value="A_APPROFONDIR">À approfondir</option>
            </select>
          </Field>
          <Field label="Validé client">
            <select value={filters.valide_client} onChange={(e) => setFilters({ ...filters, valide_client: e.target.value })}
              className="w-full px-2 py-1.5 border border-[#BFC6CC] rounded text-sm bg-white" data-testid="filter-valide-client">
              <option value="">Tous</option>
              <option value="oui">Validés client</option>
              <option value="non">Non validés</option>
            </select>
          </Field>
          <div className="flex items-end">
            <button onClick={() => setFilters({ ...EMPTY_FILTERS })}
              className="w-full px-3 py-1.5 text-xs text-[#687175] border border-[#BFC6CC] rounded hover:bg-[#F5F6F9]" data-testid="filter-clear">
              Effacer les filtres
            </button>
          </div>
        </div>
      </div>

      {selections.length > 0 && (
        <div className="mb-4 flex items-center gap-2 flex-wrap text-sm bg-[#EEF2F6] border border-[#BFC6CC] rounded-lg px-3 py-2" data-testid="selection-bar">
          <span className="text-[#687175] inline-flex items-center gap-1.5">
            {crossFilter ? <Crosshair className="w-4 h-4 text-[#397DFF]" /> : <Eye className="w-4 h-4 text-[#397DFF]" />}
            {crossFilter ? 'Filtré par' : 'Sélection (surlignage)'} :
          </span>
          {selections.map(s => (
            <span key={`${s.dim}:${s.id}`} className="inline-flex items-center gap-1 bg-white border border-[#BFC6CC] rounded-full pl-2 pr-1 py-0.5"
              data-testid={`selection-chip-${s.dim}-${s.id}`}>
              <span className="text-[10px] uppercase tracking-wide text-[#687175]">{DIM_LABEL[s.dim] || s.dim}</span>
              <span className="font-medium text-[#191D1E]">{s.label || 'Non attribué'}</span>
              <button onClick={() => removeSelection(s)} className="text-[#687175] hover:text-red-600" data-testid={`selection-remove-${s.dim}-${s.id}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
          <button onClick={() => setCrossFilter(v => !v)}
            className={`ml-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${crossFilter ? 'bg-[#397DFF] text-white hover:bg-[#2E5BCC]' : 'bg-white border border-[#397DFF] text-[#397DFF] hover:bg-blue-50'}`}
            data-testid="toggle-crossfilter"
            title="Recalculer tous les widgets en restreignant aux éléments sélectionnés">
            <Crosshair className="w-3.5 h-3.5" /> {crossFilter ? 'Mode filtrage actif' : 'Filtrer sur la sélection'}
          </button>
          <button onClick={clearSelections} className="text-[#687175] hover:text-red-600 inline-flex items-center gap-1 text-xs" data-testid="selection-clear-all">
            <X className="w-3.5 h-3.5" /> Tout effacer
          </button>
        </div>
      )}

      {isEditing && (
        <div className="bg-white border border-[#BFC6CC] rounded-xl p-3 mb-4 flex items-center gap-3 flex-wrap">
          <input type="text" value={viewName} onChange={(e) => setViewName(e.target.value)}
            placeholder="Nom de la vue"
            className="flex-1 min-w-[200px] px-3 py-1.5 border border-[#BFC6CC] rounded text-sm" data-testid="view-name-input" />
          <label className="flex items-center gap-2 text-xs text-[#687175] cursor-pointer"
            title={user?.agence_id ? '' : 'Vous devez appartenir à une agence pour partager'}>
            <input type="checkbox" checked={sharedWithAgence} onChange={(e) => setSharedWithAgence(e.target.checked)}
              disabled={!user?.agence_id} data-testid="view-shared" />
            <Share2 className="w-3 h-3" /> Partager avec mon agence
          </label>
          <button onClick={() => setBuilder({ mode: 'add', initial: { type: 'kpis', filters: {} } })}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#397DFF] text-white rounded text-sm hover:bg-[#2E5BCC]" data-testid="add-widget-btn">
            <Plus className="w-4 h-4" /> Ajouter widget
          </button>
          <button onClick={saveView} disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#10B981] text-white rounded text-sm hover:bg-[#059669] disabled:opacity-50" data-testid="save-view-btn">
            <Save className="w-4 h-4" /> {saving ? 'Enregistrement…' : (currentViewId ? 'Mettre à jour' : 'Enregistrer')}
          </button>
          {currentViewId && (
            <button onClick={deleteView}
              className="flex items-center gap-1.5 px-2 py-1.5 text-red-500 border border-red-200 rounded text-sm hover:bg-red-50" data-testid="delete-view-btn">
              <Trash2 className="w-4 h-4" /> Supprimer
            </button>
          )}
        </div>
      )}

      {feedback && (
        <div className={`mb-4 px-3 py-2 rounded text-sm ${feedback.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {feedback.message}
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        {widgets.map((w, idx) => (
          <WidgetCard key={w.id} widget={w} globalFilters={effectiveFilters} ctx={ctx}
            isEditing={isEditing}
            hasWidgetFilter={crossFilter && widgetsWithSelection.has(w.id)}
            onClearFilter={() => clearWidgetFilter(w.id)}
            onRemove={() => removeWidget(idx)}
            onDuplicate={() => duplicateWidget(idx)}
            onEdit={() => setBuilder({ mode: 'edit', index: idx, initial: w })} />
        ))}
        {widgets.length === 0 && (
          <div className="col-span-12 bg-white border-2 border-dashed border-[#BFC6CC] rounded-xl py-16 text-center">
            <p className="text-[#687175] mb-3">Aucun widget. Cliquez sur "Ajouter widget" pour commencer.</p>
            <button onClick={() => setBuilder({ mode: 'add', initial: { type: 'kpis', filters: {} } })} className="px-4 py-2 bg-[#397DFF] text-white rounded text-sm">
              <Plus className="w-4 h-4 inline mr-1" /> Ajouter mon premier widget
            </button>
          </div>
        )}
      </div>

      {builder && (
        <WidgetBuilder
          initial={builder.initial}
          agences={agences}
          commerciaux={commerciaux}
          onCancel={() => setBuilder(null)}
          onSave={saveBuilder}
        />
      )}
    </div>
  );
};

export default Analyses;
